import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@app/core/app.config';
import { VirtualTour, VirtualTourInput } from '@app/core/model/entities/asset/virtual-tour';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, from, Observable, Subject, tap } from 'rxjs';
import { map, reduce, switchMap } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class VirtualToursService {
  private apiService = inject(ApiService);
  private httpClient = inject(HttpClient);
  private appManager = inject(AppManager);
  private assetsService = inject(AssetsService);
  private appConfig = inject(AppConfig);

  private addVirtualTourSubject = new Subject<VirtualTour>();
  private updateVirtualTourSubject = new Subject<VirtualTour>();
  private deleteVirtualToursSubject = new Subject<VirtualTour[]>();

  private readonly virtualTourInfoGraphqlFragment = gql`
    fragment VirtualTourInfo on VirtualTour {
      id
      assetId
      name
      url
      entity
      properties
      computedProperties
      dataDate
    }
  `;

  /**
   * Loads a virtual tours page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch virtual tours based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the virtual tours.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of VirtualTour objects, each with additional user information.
   */
  public loadVirtualToursPage(selectParams: SelectParams): Observable<VirtualTour[]> {
    const query = gql`
      query VirtualTours($organizationId: String!, $selectParams: SelectParams!) {
        virtualTours(organizationId: $organizationId, selectParams: $selectParams) {
          ...VirtualTourInfo
        }
      }
      ${this.virtualTourInfoGraphqlFragment}
    `;
    const variables = {organizationId: this.appManager.currentOrganization.id, selectParams};

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(VirtualTour, data['virtualTours'] as VirtualTour[])));
  }

  /**
   * Loads all virtual tours pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch virtual tours based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping.
   * It also fetches information about users who created and last updated the virtual tours.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of VirtualTour objects, each with additional user information.
   */
  public loadAllVirtualTours(selectParams: SelectParams): Observable<VirtualTour[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadVirtualToursPage(params)
      .pipe(
        expand((virtualTours, i) => {
          if (virtualTours.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadVirtualToursPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as VirtualTour[])
      );
  }

  /**
   * Emits a VirtualTour once after it has been created.
   */
  public get virtualTourAdded$(): Observable<VirtualTour> {
    return this.addVirtualTourSubject.asObservable();
  }

  /**
   * Emits a VirtualTour whenever it has been updated.
   */
  public get virtualTourUpdated$(): Observable<VirtualTour> {
    return this.updateVirtualTourSubject.asObservable();
  }

  /**
   * Emits a list of VirtualTours after they have been deleted.
   */
  public get virtualToursDeleted$(): Observable<VirtualTour[]> {
    return this.deleteVirtualToursSubject.asObservable();
  }

  /**
   * Send a query to the appSheet API
   * @param tableName the table name in the appSheet
   * @param query the query body
   * @param appSheetAPI the appSheet API url
   * @param appSheetKey the appSheet application key
   * @return An observable with the response data
   * @private
   */
  private queryAppSheetAPI(
    tableName: string,
    query: any,
    appSheetAPI: string,
    appSheetKey: string
  ): Observable<Object> {
    const uri = appSheetAPI + tableName + '/Action';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'ApplicationAccessKey': appSheetKey
    });
    return this.httpClient.post(uri, query, {headers, responseType: 'json'});
  }

  /**
   * Retrieve all entities from a table in the appSheet.
   * Example: use table name 'BMA' to retrieve all Works
   * @param tableName the table name in the appSheet
   * @param appSheetAPI the appSheet API url
   * @param appSheetKey the appSheet API url
   * @param buildingId id that reference the asset in the appSheet, optional
   * @return An observable with the list of entities
   */
  public getEntities(
    tableName: string,
    appSheetAPI: string,
    appSheetKey: string,
    buildingId?: string
  ): Observable<any> {
    const selector = buildingId ? `Filter(${tableName}, [actifs_id]=${buildingId})` : '';

    const query = {
      Action: 'Find',
      Properties: {
        Selector: selector
      }
    };
    return this.queryAppSheetAPI(tableName, query, appSheetAPI, appSheetKey);
  }

  /**
   * Call the API to create a new Virtual Tour.
   * @param virtualTourInput Data for creating a new VirtualTour.
   * @return Observable emitting the new VirtualTour once the creation is completed.
   */
  public createVirtualTour(virtualTourInput: VirtualTourInput): Observable<VirtualTour> {
    const mutation = gql`
      mutation CreateVirtualTour($virtualTourInput: CreateVirtualTourInput!) {
        createVirtualTour(virtualTourInput: $virtualTourInput) {
          ...VirtualTourInfo
        }
      }
      ${this.virtualTourInfoGraphqlFragment}
    `;
    const variables = {virtualTourInput};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(VirtualTour, data['createVirtualTour'])),
        tap(newVirtualTour => this.addVirtualTourSubject.next(newVirtualTour))
      );
  }

  /**
   * Call the API to update an existing VirtualTour.
   * @param virtualTour Virtual tour entity to update.
   * @param virtualTourInput VirtualTour to update.
   * @return Updated VirtualTour.
   */
  public updateVirtualTour(virtualTour: VirtualTour, virtualTourInput: VirtualTourInput): Observable<VirtualTour> {
    const mutation = gql`
      mutation UpdateVirtualTour($virtualTourInput: UpdateVirtualTourInput!) {
        updateVirtualTour(virtualTourInput: $virtualTourInput) {
          ...VirtualTourInfo
        }
      }
      ${this.virtualTourInfoGraphqlFragment}
    `;
    const variables = {
      virtualTourInput: {
        id: virtualTour.id,
        name: virtualTour.name,
        url: virtualTour.url,
        entity: virtualTour.entity,
        ...virtualTourInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(VirtualTour, data['updateVirtualTour'])),
        tap(updatedVirtualTour => this.updateVirtualTourSubject.next(updatedVirtualTour))
      );
  }

  /**
   * Call the API to update the Asset main Virtual Tour.
   * @param assetId Asset's ID
   * @param virtualTourId Virtual Tour's ID
   * @return Updated Virtual Tours
   */
  public setAssetMainVirtualTour(assetId: string, virtualTourId: string): Observable<VirtualTour> {
    const mutation = gql`
      mutation SetAssetMainVirtualTour($assetId: String!, $virtualTourId: String!) {
        setAssetMainVirtualTour(assetId: $assetId, virtualTourId: $virtualTourId) {
          ...VirtualTourInfo
        }
      }${this.virtualTourInfoGraphqlFragment}
    `;
    const variables = {
      assetId: assetId,
      virtualTourId: virtualTourId
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        switchMap(data => from(data['setAssetMainVirtualTour'] as VirtualTour[])),
        map(virtualTour => plainToInstance(VirtualTour, virtualTour)),
        tap(virtualTour => this.updateVirtualTourSubject.next(virtualTour))
      );
  }


  /**
   * Make an API request to delete Virtual Tours.
   * @param virtualTours Virtual Tours to delete.
   * @return True if all VirtualTours have been deleted successfully, false otherwise.
   */
  public deleteVirtualTours(virtualTours: VirtualTour[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteVirtualTours($virtualTourIds: [String!]!) {
        deleteVirtualTours(ids: $virtualTourIds)
      }
    `;
    const variables = {virtualTourIds: virtualTours.map(virtualTour => virtualTour.id)};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteVirtualTours'] as boolean),
        tap(success => {
          if (success) {
            this.deleteVirtualToursSubject.next(virtualTours);
          }
        })
      );
  }

  /**
   * Navigate to the virtual tours tab of the Asset's sheet.
   * @param assetId Id of the Asset to navigate to.
   * @return Promise emitting true if the navigation is a success, otherwise false.
   */
  public async navigateToAssetSheet(assetId: string): Promise<void> {
    await this.assetsService.navigateToAssetSheet(assetId, 'virtual-tour');
  }
}
